import { merge } from '../../helper';
import isEmptyObject from '../../general/isEmpty/isEmptyObject';
import popObject from '../../general/popObject';

// ----------------------------------------------------------------------------

function start(config: Record<string, any> = {}): ReturnType<typeof manager> {
    //
    const managerHistory = manager(config);

    //
    return managerHistory;
}

function manager(config: Record<string, any> = {}): any {
    // Source
    const src: Record<string, any> = {
        undo: [] as any[],
        redo: [] as any[],
        config: {},
    };

    // ------------------------------------------------------------------------

    function init(): any {
        // Merge
        merge(src.config, config);
    }

    // ------------------------------------------------------------------------

    function get(): any {
        return src;
    }

    function getUndo(): any {
        return src.undo;
    }

    function getRedo(): any {
        return src.redo;
    }

    // ------------------------------------------------------------------------

    function addUndo(data: any): void {
        // Undo Push
        src.undo.push(data);

        // Reset
        src.redo = [];
    }

    function addRedo(data: any): void {
        // Redo Push
        src.redo.push(data);
    }

    // ------------------------------------------------------------------------

    function undo(): any {
        // If there is no undo
        if (!existUndo()) {
            return;
        }

        // Pop last set of data from undo
        const itemUndo = src.undo.pop();

        // Push set of data to redo
        src.redo.push(itemUndo);

        // Return
        return itemUndo;
    }

    function redo(): any {
        // If there is no redo
        if (!existRedo()) {
            return;
        }

        // Pop last set of data from redo
        const itemRedo = src.redo.pop();

        // Push set of data to undo
        src.undo.push(itemRedo);

        // Return
        return itemRedo;
    }

    // ------------------------------------------------------------------------

    function existUndo(): any {
        return src.undo?.length;
    }

    function existRedo(): any {
        return src.redo?.length;
    }

    // ------------------------------------------------------------------------

    function clearUndo(): any {
        src.undo = [];
    }

    function clearRedo(): any {
        src.redo = [];
    }

    // ------------------------------------------------------------------------

    function purge(): any {
        src.undo = [];
        src.redo = [];
    }

    // ------------------------------------------------------------------------

    init(); // Ready

    return {
        config: src.config,
        get,
        getUndo,
        getRedo,
        addUndo,
        addRedo,
        undo,
        redo,
        existUndo,
        existRedo,
        clearUndo,
        clearRedo,
        purge,
    };
}

function managerAd(config: Record<string, any> = {}): any {
    //
    const defaultSource: Record<string, any> = {
        count: 0,
        undo: {},
        redo: {},
        config: {},
    };

    //
    let src = defaultSource;

    // ------------------------------------------------------------------------

    function init(): any {
        // Merge
        merge(src.config, config);
    }

    // ------------------------------------------------------------------------

    function get(): any {
        return src;
    }

    function getUndo(type: string): any[] {
        return findUndo(type);
    }

    function getRedo(type: string): any[] {
        return findRedo(type);
    }

    // ------------------------------------------------------------------------

    function add(type: string, data: any): void {
        addUndo(type, data);
    }

    function addUndo(type: string, data: any): void {
        //
        if (!type || typeof type !== 'string') {
            return;
        }

        //
        const item = {
            type: type,
            data: data,
            index: src.count,
        };

        //
        src.undo[src.count] = item;

        //
        src.count++;
    }

    function addRedo(type: string, data: any): void {
        //
        if (!type || typeof type !== 'string') {
            return;
        }

        //
        const item = {
            type: type,
            data: data,
            index: src.count,
        };

        //
        src.redo.push(item);

        //
        src.count++;
    }

    // ------------------------------------------------------------------------

    function findUndo(find: string): any[] {
        const list: any[] = [];

        for (const key of Object.keys(src.undo)) {
            const value = src.undo[key];

            if (value.type === find) {
                list.push(value);
            }
        }

        return list;
    }

    function findRedo(find: string): any[] {
        const list: any[] = [];

        for (const key of Object.keys(src.redo)) {
            const value = src.redo[key];

            if (value.type === find) {
                list.push(value);
            }
        }

        return list;
    }

    // ------------------------------------------------------------------------

    function undo(type: string | null = null): any {
        // If there is no undo
        if (isEmptyObject(src.undo)) {
            return;
        }

        //
        let itemUndo;

        // If using type
        if (type && typeof type === 'string') {
            // Get find undo
            const listUndo = findUndo(type);

            // If there is no list undo
            if (!listUndo?.length) {
                console.error('No found type history...');
                return;
            }

            // Pop last set of data from undo
            itemUndo = listUndo.pop();

            // Push set of data to redo
            src.redo[itemUndo.index] = itemUndo;
        }
        // Is default
        else {
            //
            if (type !== null) {
                console.error('No found type history...');
                return;
            }

            // Pop last set of data from undo
            itemUndo = popObject(src.undo);

            // Push set of data to redo
            src.redo[itemUndo.key] = itemUndo.value;
        }

        //
        return itemUndo;
    }

    function redo(type: string | null = null): any {
        // If there is no redo
        if (isEmptyObject(src.redo)) {
            return;
        }

        //
        let itemRedo;

        // If using type
        if (type && typeof type === 'string') {
            // Get find redo
            const listRedo = findRedo(type);

            // If there is no list redo
            if (!listRedo?.length) {
                console.error('No found type history...');
                return;
            }

            // Pop last set of data from redo
            itemRedo = listRedo.pop();

            // Push set of data to undo
            src.undo[itemRedo.index] = itemRedo;
        }
        // Is default
        else {
            //
            if (type !== null) {
                console.error('No found type history...');
                return;
            }

            // Pop last set of data from redo
            itemRedo = popObject(src.redo);

            // Push set of data to undo
            src.undo[itemRedo.key] = itemRedo.value;
        }

        // Return
        return itemRedo;
    }

    // ------------------------------------------------------------------------

    function clear(type: string): void {
        const undoRaw: unknown = src.undo;
        const redoRaw: unknown = src.redo;

        if (typeof undoRaw === 'object' && undoRaw !== null) {
            const next: Record<string, unknown> = {};
            for (const [key, value] of Object.entries(undoRaw)) {
                if (!(typeof value === 'object' && value !== null && 'type' in value && value.type === type)) {
                    next[key] = value;
                }
            }
            src.undo = next;
        }

        if (typeof redoRaw === 'object' && redoRaw !== null) {
            const next: Record<string, unknown> = {};
            for (const [key, value] of Object.entries(redoRaw)) {
                if (!(typeof value === 'object' && value !== null && 'type' in value && value.type === type)) {
                    next[key] = value;
                }
            }
            src.redo = next;
        }
    }

    function clearUndo(): any {
        src.undo = { ...defaultSource.undo };
    }

    function clearRedo(): any {
        src.redo = { ...defaultSource.redo };
    }

    // ------------------------------------------------------------------------

    function purge(): any {
        src = { ...defaultSource };
    }

    // ------------------------------------------------------------------------

    init(); // Ready

    return {
        config: src.config,
        get,
        getUndo,
        getRedo,
        add,
        addUndo,
        addRedo,
        undo,
        redo,
        clear,
        clearUndo,
        clearRedo,
        purge,
    };
}

// ----------------------------------------------------------------------------

export { start, manager, managerAd };
